import React, { useState, useRef } from "react";
import { Button, Progress, message, Space, Typography } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { uploadFile } from "../../../api/attachmentApi";

const { Text } = Typography;

/**
 * 附件上传组件
 * 支持拖拽上传和点击上传，显示上传进度
 */
const AttachmentUpload = ({
    onAttachmentAdd,
    onUploadSuccess,
    onUploadError,
    multiple = false,
    accept = ".pdf,.xlsx,.xls,.docx,.doc,.txt,.jpg,.jpeg,.png,.gif,.zip",
    maxSize = 100 * 1024 * 1024, // 100MB
    disabled = false,
    showUploadList = false,
    beforeUpload, // 上传前钩子，返回 false 或 Promise<false> 可阻止上传
    hint, // 自定义提示文案
    isTemporary = false, // 是否为临时文件（默认 false）
    onFilesSelected, // 新增：批量文件选择回调，如果提供，将跳过默认的单文件处理逻辑
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentFileName, setCurrentFileName] = useState("");
    const fileInputRef = useRef(null);

    /**
     * 验证文件
     */
    const validateFile = (file) => {
        // 检查文件大小
        if (file.size > maxSize) {
            const maxSizeMB = Math.round(maxSize / 1024 / 1024);
            message.error(`文件大小不能超过 ${maxSizeMB}MB`);
            return false;
        }

        // 检查文件类型
        const allowedTypes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
            "application/vnd.ms-excel", // xls
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
            "application/msword", // doc
            "text/plain",
            "image/jpeg",
            "image/png",
            "image/gif",
            "application/zip",
            "application/x-zip-compressed",
        ];

        if (!allowedTypes.includes(file.type)) {
            message.error("不支持的文件类型");
            return false;
        }

        return true;
    };

    /**
     * 处理文件上传
     */
    const handleUpload = async (file) => {
        if (!validateFile(file)) {
            return false;
        }

        // 执行上传前检查
        if (beforeUpload) {
            try {
                const result = await beforeUpload(file);
                if (result === false) {
                    return false;
                }
            } catch (error) {
                console.error("上传前检查失败:", error);
                return false;
            }
        }

        setUploading(true);
        setUploadProgress(0);
        setCurrentFileName(file.name);

        try {
            const result = await uploadFile(
                file,
                (progress) => {
                    setUploadProgress(Math.round(progress));
                },
                isTemporary,
            ); // 传递临时文件标记

            message.success(`文件 "${file.name}" 上传成功`);

            const attachmentData = {
                ...result,
                file_size: file.size,
                content_type: file.type,
            };

            // 调用附件添加回调
            if (onAttachmentAdd) {
                onAttachmentAdd(attachmentData);
            }

            // 调用成功回调（向后兼容）
            if (onUploadSuccess) {
                onUploadSuccess(attachmentData);
            }
        } catch (error) {
            console.error("上传失败:", error);
            message.error(`文件 "${file.name}" 上传失败: ${error.message}`);

            // 调用错误回调
            if (onUploadError) {
                onUploadError(error, file);
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setCurrentFileName("");
        }

        return false; // 阻止antd的默认上传行为
    };

    /**
     * 处理文件选择
     */
    const handleFileSelect = (info) => {
        const { fileList } = info;

        // 处理新添加的文件
        const newFiles = fileList.filter((item) => item.status === "uploading" || !item.status);

        newFiles.forEach((item) => {
            if (item.originFileObj) {
                handleUpload(item.originFileObj);
            }
        });
    };

    /**
     * 自定义上传请求（阻止antd的默认上传）
     */
    const customRequest = ({ file, onSuccess, onError }) => {
        handleUpload(file)
            .then(() => {
                onSuccess();
            })
            .catch((error) => {
                onError(error);
            });
    };

    return (
        <div className="attachment-upload">
            {uploading && (
                <div style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text type="secondary">正在上传: {currentFileName}</Text>
                        <Progress percent={uploadProgress} status={uploadProgress === 100 ? "success" : "active"} showInfo={true} />
                    </Space>
                </div>
            )}

            {/* 简化的上传按钮 */}
            <div style={{ textAlign: "left" }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple={multiple}
                    accept={accept}
                    style={{ display: "none" }}
                    onChange={(e) => {
                        const files = Array.from(e.target.files);
                        if (onFilesSelected && files.length > 0) {
                            // 批量处理模式：交给父组件统一处理
                            onFilesSelected(files);
                        } else {
                            // 单文件模式：逐个处理
                            files.forEach((file) => handleUpload(file));
                        }
                        e.target.value = ""; // 清空input，允许重复选择相同文件
                    }}
                />
                <Space align="start">
                    <Button icon={<UploadOutlined />} disabled={disabled || uploading} onClick={() => fileInputRef.current?.click()} type="dashed">
                        选择文件
                    </Button>
                    <Text type="secondary" style={{ fontSize: "12px", lineHeight: "1.5", display: "inline-block", paddingTop: "4px" }}>
                        {hint || `或拖拽文件到窗口任意位置上传 （支持 PDF、Excel、Word、图片、ZIP 等格式，单个文件最大 ${Math.round(maxSize / 1024 / 1024)}MB）`}
                    </Text>
                </Space>
            </div>
        </div>
    );
};

export default AttachmentUpload;
